import React, { useEffect, useState, useCallback } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Stack,
    IconButton,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    Card,
    MenuItem,
    ListItemText,
    FormHelperText,
    styled,
    Button
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from "react-router-dom"
import { useFormik } from 'formik';

import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from "../common/CommonSectionHeader";
import CommonRestricted from '../common/CommonRestricted';

import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { useCustomFields } from "../../hooks/api/useCustomFields";
import { useSystemPrompt } from "../../hooks/useSystemPrompt";
import { usePermissions, scopeIds, scopeTypes, functionTypes as permissionFunctionTypes } from '../../hooks/usePermissions';
import { useTranslations } from '../../hooks/useTranslations';

import { mergeSchema } from "../../utils/dataSchema";
import { useAppContextFeatures } from '../../context/AppContext';

import {
    initialValues,
    validationSchema,
    validationSchemaRows,
    initialValuesRow,
    moduleTypes,
    functionTypes,
    getCustomFieldsDefinitions,
    inputTypes,
    inputTypesDef
} from "../../schema/customFields/customFieldSchema";

import { CommonIcons, icons } from "../common/CommonIcons";

import { capitalizeString } from "../../utils/formatString"

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

export default function UpdateCustomField({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update } = useCustomFields();
    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.customField.id, scopeTypes.customField.customField.id)

    const { setSystemLoading } = useAppContextFeatures();
    const [savedValues, setSavedValues] = useState(null)

    const [inputType, setInputType] = useState("")

    const [selection, setSelection] = useState(getCustomFieldsDefinitions())

    const getSchema = ({ inputType }) => {

        if (inputType === inputTypesDef.textbox || inputType === "") {
            return validationSchema
        }

        return mergeSchema(validationSchema, validationSchemaRows);
    }

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { custom_field: customField, options: dataOptions } = data;

            // construct rows
            const rows = dataOptions.map(i => {
                return {
                    name: i?.option_name
                }
            })

            setSavedValues({
                name: customField.field_name,
                label: customField.field_label,
                module: customField.field_module,
                inputType: customField.field_type,
                description: customField.field_description ? customField.field_description : "",
                function: customField.module_function,
                defaultValue: customField.default_value,
                status: customField?.status === '1',
                rows
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        try {

            const options =
                Array.isArray(currentValues?.rows) ?
                    currentValues?.rows.map(i => ({ "option_name": i.name }))
                    :
                    []

            let data = {
                custom_field: {
                    field_label: currentValues?.label,
                    field_description: currentValues?.description ? currentValues.description : "",
                    default_value: "",
                    status: currentValues?.status ? 1 : 0,
                },
                options,
                rules: []
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/customFields"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema({ inputType }),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;


    useEffect(() => {

        loadData(id);

    }, [])

    useEffect(() => {

        if (values)
            setInputType(values.inputType)

    }, [values])

    const handleAddOption = ({ rows, setFieldValue }) => {

        rows.push(initialValuesRow);
        setFieldValue('rows', rows)

    }

    const handleRemoveOption = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, t(`itemsAtLeastHave1Row`))
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        setFieldValue('rows', newRows)

        return true;

    }

    const getOptions = ({ moduleType }) => {

        const selected = selection.filter(i => i.id === moduleType)

        return selected.length ? selected[0].options : []

    }

    const RenderDropdownRow = ({ index, itemValues, itemTouched, itemErrors }) => {
        return (
            <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={`rows.${index}.name`}
                label={t("name")}
                type="text"
                variant="outlined"
                value={itemValues?.name}
                onChange={handleChange}
                error={itemTouched?.name && Boolean(itemErrors?.name)}
                helperText={itemTouched?.name && itemErrors?.name}
            />
        )
    }

    const RenderDropdownRows = () => {

        if (values.inputType === inputTypesDef.textbox || values.inputType === "") return <></>

        const { rows } = values

        return (
            <Grid container spacing={0}>
                <Grid item md={12} sx={{ my: 2 }}>
                    <Divider />
                </Grid>
                <Grid item md={12}>
                    <Grid container>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('dropdownOptions')}
                                subTitle={t('Selection options of the custom field')}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            {
                                rows && rows.length > 0
                                && rows.map((item, index) => {

                                    const itemValues = item;
                                    let itemTouched = touched?.rows
                                    let itemErrors = errors?.rows
                                    // const itemTouched = touched?.rows

                                    if (itemTouched) {
                                        itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                                    }

                                    if (itemErrors) {
                                        itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                                    }

                                    return (
                                        <Grid container key={index}>
                                            <Grid item md={1} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
                                                <IconButton
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveOption({ rows: values.rows, index, setFieldValue })}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            </Grid>
                                            <Grid item md={11}>
                                                {
                                                    RenderDropdownRow({
                                                        key: index,
                                                        index,
                                                        itemValues,
                                                        itemTouched,
                                                        itemErrors
                                                    })
                                                }
                                                {/* <RenderDropdownRow 
                                                    key={index} 
                                                    index={index} 
                                                    itemValues={itemValues}
                                                    itemTouched={itemTouched}
                                                    itemErrors={itemErrors}
                                                /> */}
                                            </Grid>
                                        </Grid>
                                    )

                                })
                            }
                            <Button
                                variant="outlined"
                                color={"primary"}
                                fullWidth
                                sx={{
                                    mt: 1,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: "center"
                                }}
                                onClick={() => handleAddOption({ rows: values.rows, setFieldValue })}
                            >
                                <CommonIcons name={icons.add} />
                                <Box sx={{ ml: 1 }}>
                                    {t('addOption')}
                                </Box>
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('properties')}
                                subTitle={t('detailSettingsOfTheCustomField')}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>

                            <Grid item xs={12} md={12} textAlign="left">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="status"
                                            checked={Boolean(values.status)}
                                            onChange={(event, checked) => {
                                                setFieldValue("status", checked);
                                            }}
                                        />
                                    }
                                    label={
                                        Boolean(values.status) ? t("enabled") : t("disabled")
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={12} textAlign="left">
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            autoFocus
                                            fullWidth
                                            name={"name"}
                                            label={t("name")}
                                            type="text"
                                            variant="outlined"
                                            value={values?.name}
                                            // onChange={handleChange}
                                            // error={touched.name && Boolean(errors.name)}
                                            // helperText={touched.name && errors.name}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            fullWidth
                                            name={"label"}
                                            label={t("displayLabel")}
                                            type="label"
                                            variant="outlined"
                                            value={values?.label}
                                            onChange={handleChange}
                                            error={touched.label && Boolean(errors.label)}
                                            helperText={touched.label && errors.label}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <FormControl
                                            margin="dense"
                                            fullWidth
                                            size="small"
                                            error={Boolean(touched.module) && Boolean(errors.module)}
                                        >
                                            <InputLabel id="module">{t('moduleType')}</InputLabel>
                                            <Select
                                                labelId="module"
                                                label={t('moduleType')}
                                                name={"module"}
                                                // onChange={handleChange}
                                                value={values.module}
                                                disabled
                                            >
                                                {
                                                    selection.map((i, index) => {
                                                        return (
                                                            <MenuItem
                                                                key={index}
                                                                value={i.id}
                                                            >
                                                                <ListItemText primary={`${capitalizeString({ val: i.display })}`} />
                                                            </MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                            <FormHelperText>
                                                {touched.module && errors.module}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <FormControl
                                            margin="dense"
                                            fullWidth
                                            size="small"
                                            error={Boolean(touched.function) && Boolean(errors.function)}
                                        >
                                            <InputLabel id="function">{t('functionType')}</InputLabel>
                                            <Select
                                                labelId="function"
                                                label={t('functionType')}
                                                name={"function"}
                                                // onChange={handleChange}
                                                value={values.function}
                                                disabled
                                            >
                                                {
                                                    getOptions({ moduleType: values.module }).map((i, index) => {
                                                        return (
                                                            <MenuItem
                                                                key={index}
                                                                value={i.id}
                                                            >
                                                                <ListItemText primary={`${capitalizeString({ val: i.display })}`} />
                                                            </MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                            <FormHelperText>
                                                {touched.function && errors.function}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            fullWidth
                                            name={"description"}
                                            label={t("description")}
                                            type="text"
                                            multiline
                                            rows={3}
                                            variant="outlined"
                                            value={values?.description}
                                            onChange={handleChange}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                        </Grid>

                        <Grid item md={12} sx={{ my: 2 }}>
                            <Divider />
                        </Grid>

                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('inputs')}
                                subTitle={t('inputSettingsOfTheCustomField')}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>

                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                    // error={touched.inputType && Boolean(errors.inputType)}
                                    >
                                        <InputLabel id="inputType">{t('inputType')}</InputLabel>
                                        <Select
                                            labelId="inputType"
                                            label={t('inputType')}
                                            name={"inputType"}
                                            // onChange={handleChange}
                                            value={values.inputType}
                                            disabled
                                        >
                                            {
                                                inputTypes.map((i, index) => {
                                                    return (
                                                        <MenuItem
                                                            key={index}
                                                            value={i.id}
                                                        >
                                                            {/* <Checkbox checked={values.inputType.indexOf(i.id) > -1} /> */}
                                                            <ListItemText primary={`${i.display}`} />
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                        {/* <FormHelperText>
                                            {touched.inputType && errors.inputType}
                                        </FormHelperText> */}
                                    </FormControl>
                                </Grid>
                                {/* <Grid item xs={12} sm={12} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"defaultValue"}
                                        label="Default Value"
                                        type="defaultValue"                                         
                                        variant="outlined"
                                        value={values?.defaultValue}
                                        onChange={handleChange}
                                        error={touched.defaultValue && Boolean(errors.defaultValue)}
                                        helperText={touched.defaultValue && errors.defaultValue}
                                    />
                                </Grid> */}


                            </Grid>

                        </Grid>

                        {
                            RenderDropdownRows()
                        }

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateCustomField })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateCustomField")}
                breadcrumbs={[
                    {
                        displayText: t("customFields"),
                        props: {
                            href: getRoute("settings/customFields")
                        }
                    },
                    {
                        displayText: t("updateCustomField"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: permissionFunctionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}